import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import {
  Target, Loader2, Lightbulb, Sparkles, ArrowRight, Check, X, ArrowLeft, TrendingUp, RotateCcw,
} from "lucide-react";
import { api, recordMastery } from "@/lib/api";
import { SpeakButton } from "@/components/SpeakButton";

export const FocusMode = ({ lecture, weakConcepts, onBack, onUpdate }) => {
  const [phase, setPhase] = useState("loading"); // loading | refresher | quiz | result | error
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [focusAnswers, setFocusAnswers] = useState([]);

  useEffect(() => {
    let active = true;
    setPhase("loading");
    api
      .post(`/lectures/${lecture.id}/focus`, { weak_concepts: weakConcepts })
      .then((res) => {
        if (!active) return;
        if (!res.data.quiz?.length) return setPhase("error");
        setData(res.data);
        setPhase("refresher");
      })
      .catch(() => active && setPhase("error"));
    return () => { active = false; };
  }, [lecture.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const quiz = data?.quiz || [];
  const q = quiz[idx];
  const total = quiz.length;
  const pct = total ? Math.round((correctCount / total) * 100) : 0;

  const pick = (i) => {
    if (locked) return;
    setSelected(i);
    setLocked(true);
    const correct = i === q.correct_index;
    if (correct) setCorrectCount((c) => c + 1);
    setFocusAnswers((a) => [...a, { concept: q.concept || "General", correct }]);
  };

  const next = () => {
    if (idx + 1 >= total) {
      setPhase("result");
      const finalPct = Math.round((correctCount / total) * 100);
      if (finalPct >= 80) confetti({ particleCount: 120, spread: 90, origin: { y: 0.6 } });
      const map = {};
      focusAnswers.forEach((a) => {
        if (!map[a.concept]) map[a.concept] = { concept: a.concept, correct: 0, total: 0 };
        map[a.concept].total += 1;
        if (a.correct) map[a.concept].correct += 1;
      });
      recordMastery(lecture.id, Object.values(map))
        .then((u) => onUpdate && onUpdate(u))
        .catch(() => {});
    } else {
      setIdx((i) => i + 1);
      setSelected(null);
      setLocked(false);
    }
  };

  const retry = () => {
    setIdx(0); setSelected(null); setLocked(false); setCorrectCount(0); setFocusAnswers([]); setPhase("refresher");
  };

  // ---- Loading ----
  if (phase === "loading") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="inline-block">
          <Target className="h-10 w-10 text-accent" />
        </motion.div>
        <h2 className="mt-6 font-serif text-2xl font-semibold">Building your Focus session…</h2>
        <p className="mt-2 text-muted-foreground">
          Crafting a targeted refresher + mini-quiz on: {weakConcepts.join(", ")}
        </p>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h2 className="font-serif text-2xl font-semibold">Couldn't build Focus Mode</h2>
        <p className="mt-2 text-muted-foreground">Please try again in a moment.</p>
        <button onClick={onBack} className="mt-6 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold">
          Back to Results
        </button>
      </div>
    );
  }

  // ---- Refresher ----
  if (phase === "refresher") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button data-testid="focus-back-btn" onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to results
        </button>
        <div className="flex items-center gap-2 text-accent mb-2">
          <Target className="h-5 w-5" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Focus Mode · Refresher</span>
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold">Let's shore up your weak spots</h1>
        <p className="mt-2 text-muted-foreground">
          A quick re-explanation of the {data.refresher.length} concept{data.refresher.length !== 1 && "s"} you missed, then a short mini-quiz.
        </p>

        <div className="mt-6 space-y-4">
          {data.refresher.map((r, i) => (
            <motion.div
              key={i}
              data-testid={`focus-refresher-${i}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="rounded-2xl border border-border bg-card p-6 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4 text-accent" />
                <h3 className="font-serif text-xl font-semibold">{r.concept}</h3>
              </div>
              <p className="text-sm text-foreground/85 leading-relaxed">{r.explanation}</p>
              {r.tip && (
                <div className="mt-3 flex items-start gap-2 rounded-xl bg-secondary/60 p-3">
                  <Sparkles className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  <p className="text-sm text-foreground/80"><span className="font-semibold">Tip:</span> {r.tip}</p>
                </div>
              )}
              <div className="mt-3">
                <SpeakButton
                  text={`${r.concept}. ${r.explanation} ${r.tip ? "Tip: " + r.tip : ""}`}
                  testid={`focus-speak-${i}`}
                  label="Listen"
                />
              </div>
            </motion.div>
          ))}
        </div>

        <button
          data-testid="focus-start-quiz-btn"
          onClick={() => setPhase("quiz")}
          className="mt-8 w-full flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground py-3.5 font-semibold hover:opacity-90 transition"
        >
          Start Mini-Quiz ({total} questions) <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // ---- Result ----
  if (phase === "result") {
    const improved = pct >= 70;
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="rounded-3xl border border-border bg-card shadow-lg p-8 sm:p-12">
          <div className="flex justify-center mb-3 text-accent">
            <TrendingUp className="h-8 w-8" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Focus Mini-Quiz</span>
          <h2 data-testid="focus-result-score" className="mt-2 font-serif text-5xl font-bold">{pct}%</h2>
          <p className="mt-2 text-lg font-semibold">{correctCount} of {total} correct</p>
          <p className="mt-2 text-muted-foreground">
            {improved ? "Great progress — those concepts are sticking now!" : "Keep at it — review the refresher once more and try again."}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button data-testid="focus-retry-btn" onClick={retry} className="flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3 font-semibold hover:border-primary/50 transition">
              <RotateCcw className="h-4 w-4" /> Retry Focus Quiz
            </button>
            <button data-testid="focus-done-btn" onClick={onBack} className="rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold hover:opacity-90 transition">
              Back to Results
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---- Mini-Quiz ----
  const isCorrect = selected === q.correct_index;
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-3">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-accent">
          <Target className="h-4 w-4" /> Focus · {idx + 1} / {total}
        </span>
        <span className="text-sm font-mono text-muted-foreground">{correctCount} correct</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden mb-8">
        <motion.div className="h-full bg-accent rounded-full" animate={{ width: `${(idx / total) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold leading-snug mb-6">{q.question}</h2>
          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const chosen = selected === i;
              const showCorrect = locked && i === q.correct_index;
              const showWrong = locked && chosen && i !== q.correct_index;
              return (
                <button
                  key={i}
                  data-testid={`focus-option-${i}`}
                  onClick={() => pick(i)}
                  disabled={locked}
                  className={`w-full text-left flex items-center gap-4 rounded-2xl border-2 px-5 py-4 transition-all ${
                    showCorrect ? "border-[hsl(var(--success))] bg-[hsl(var(--success)/0.1)]"
                    : showWrong ? "border-destructive bg-destructive/10"
                    : locked ? "border-border opacity-60"
                    : "border-border hover:border-accent hover:-translate-y-0.5"
                  }`}
                >
                  <span className={`grid place-items-center h-8 w-8 rounded-full font-semibold text-sm shrink-0 ${
                    showCorrect ? "bg-[hsl(var(--success))] text-white" : showWrong ? "bg-destructive text-white" : "bg-secondary"
                  }`}>
                    {showCorrect ? <Check className="h-4 w-4" /> : showWrong ? <X className="h-4 w-4" /> : String.fromCharCode(65 + i)}
                  </span>
                  <span className="font-medium">{opt}</span>
                </button>
              );
            })}
          </div>

          {locked && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-5">
              <div className={`rounded-2xl p-5 ${isCorrect ? "bg-[hsl(var(--success)/0.1)]" : "bg-secondary"}`}>
                <p className={`font-semibold mb-1 ${isCorrect ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                  {isCorrect ? "Correct!" : "Not quite."}
                </p>
                {q.explanation && <p className="text-sm text-foreground/80 leading-relaxed">{q.explanation}</p>}
              </div>
              <button
                data-testid="focus-next-btn"
                onClick={next}
                className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground py-3.5 font-semibold hover:opacity-90 transition"
              >
                {idx + 1 >= total ? "See Focus Score" : "Next"} <ArrowRight className="h-4 w-4" />
              </button>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
